import { SearchPageSearchParams } from "@/types";
import { RecordModel } from "pocketbase";

export const toBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const formatTimeAgo = (date: Date) => {
  const now = new Date();
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60)
  );

  if (diffInMinutes < 60) {
    return `${diffInMinutes} min`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""}`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""}`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  return `${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""}`;
};

export const capitalize = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Names come back from the database with missing parts stringified, so a user
// with no first/last name reads as "null null" or "undefined undefined".
const PLACEHOLDER_NAME_PARTS = new Set(["null", "undefined", "n/a", "-"]);

const cleanNamePart = (part: unknown): string => {
  const value = typeof part === "string" ? part.trim() : "";
  return PLACEHOLDER_NAME_PARTS.has(value.toLowerCase()) ? "" : value;
};

export const getBreederDisplayName = (
  user?: Record<string, any> | null
): string => {
  const displayName = cleanNamePart(user?.displayName)
    .split(/\s+/)
    .map(cleanNamePart)
    .filter(Boolean)
    .join(" ");
  if (displayName) return displayName;

  const fullName = [cleanNamePart(user?.firstName), cleanNamePart(user?.lastName)]
    .filter(Boolean)
    .join(" ");

  return fullName || "Private Breeder";
};

// Advert descriptions are authored in a rich-text editor and stored as HTML.
// Strip tags for plain-text contexts (card previews, SEO meta, share sheets) so
// markup like <p> doesn't show up as literal text.
export const stripHtml = (html: string): string =>
  (html ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

export const formatPrice = (
  price: number,
  currency: string = "USD"
): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

export const getFileUrl = (
  collectionId: string,
  itemId: string,
  fileName: string
): string => {
  // console.log('test file', `${process.env.NEXT_PUBLIC_POCKETBASE_URL}/api/files/${collectionId}/${itemId}/${fileName}`)
  return `${process.env.NEXT_PUBLIC_POCKETBASE_URL}/api/files/${collectionId}/${itemId}/${fileName}`;
};

export function base64ToBytes(fileString: string) {
  const byteString = atob(fileString.split(",")[1]);
  const mimeString = fileString.split(",")[0].split(":")[1].split(";")[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const file = new Blob([ab], { type: mimeString });
  return file;
}

export const getCommodityTypeFromParams = (
  petTypeOrCommodityOrPetTypeAndAccessories: string,
  petTypeCollection: RecordModel[],
  commodityTypeCollection: RecordModel[],
  commodity?: any
) => {
  switch (true) {
    case commodity != undefined:
      return commodity?.expand?.commodityType;
    case petTypeOrCommodityOrPetTypeAndAccessories?.includes("accessories"):
      return commodityTypeCollection.find(
        (ct) => ct.collectiveFriendlyName == "accessories"
      );
    case petTypeCollection.some(
      (pt) => pt.pluralName == petTypeOrCommodityOrPetTypeAndAccessories
    ):
      return commodityTypeCollection.find(
        (ct) => ct.collectiveFriendlyName == "pets"
      );
    case commodityTypeCollection.find(
      (ct) =>
        ct.collectiveFriendlyName == petTypeOrCommodityOrPetTypeAndAccessories
    ) != undefined:
      return commodityTypeCollection.find(
        (ct) =>
          ct.collectiveFriendlyName == petTypeOrCommodityOrPetTypeAndAccessories
      );
    default:
      return undefined;
  }
};

export const getPetTypeFromParams = (
  petTypeOrCommodityOrPetTypeAndAccessories: string,
  petTypeCollection: RecordModel[],
  commodityTypeCollection: RecordModel[],
  searchParams: SearchPageSearchParams,
  commodity?: any
) => {
  switch (true) {
    case commodity != undefined:
      return commodity?.expand?.petType;
    case petTypeOrCommodityOrPetTypeAndAccessories?.includes("accessories"):
      return petTypeCollection.find(
        (pt) =>
          pt.name ==
          petTypeOrCommodityOrPetTypeAndAccessories.replace("-accessories", "")
      );
    case petTypeCollection.find(
      (pt) => pt.pluralName == petTypeOrCommodityOrPetTypeAndAccessories
    ) != undefined:
      return petTypeCollection.find(
        (pt) => pt.pluralName == petTypeOrCommodityOrPetTypeAndAccessories
      );
    case petTypeCollection.find((pt) => pt.name == searchParams.petType) !=
      undefined:
      console.log("found search param");
      return petTypeCollection.find((pt) => pt.name == searchParams.petType);
    case commodityTypeCollection.find(
      (ct) =>
        ct.collectiveFriendlyName == petTypeOrCommodityOrPetTypeAndAccessories
    ) != undefined:
      console.log("found collective", searchParams);
      return undefined;
    default:
      return undefined;
  }
};

export const getLocationFromParams = async (
  params: Record<string, any>,
  getLocationByPostcode: (postcode: string, locationName?: string) => Promise<any>,
  getLocationByCommonName: (locationName: string) => Promise<any>,
) => {
  const locationParam = params.location;

  if (locationParam) {
    const postcodeMatch = locationParam.match(/(\d{4})$/);
    const postcode = postcodeMatch ? postcodeMatch[1] : undefined;
    const locationName = locationParam.replace(`-${postcode}`, "");

    if (postcode) {
      return await getLocationByPostcode(postcode, locationName);
    } else {
      return await getLocationByCommonName(locationName)
    }
  }
};

export const getAdTypeFromCommodityType = (
  ct: RecordModel,
  adTypeCollection: RecordModel[]
) => {
  return adTypeCollection.filter((adType) => ct?.adType?.includes(adType.id));
};

export const getAdTypeFromParams = (adTypeParam: string, adTypeCollection: RecordModel[]) => {
  return adTypeCollection.find(adType => adType.name === adTypeParam);
};

export const formatLocationName = (location: RecordModel): string => {
  const formattedName = location.locationName.replace(/\s+/g, '-').toLowerCase();
  return `${formattedName}-${location.postcode}`;
};

/**
 * Whole days left on a boost, or null when there is no usable date.
 *
 * PocketBase stores dates as "2026-08-10 05:42:58.933Z". That space form is not
 * reliably parsed by Date across engines, so it is normalised to ISO first.
 */
export const getBoostDaysLeft = (boostedUntil?: string): number | null => {
  if (!boostedUntil) return null;
  const until = new Date(String(boostedUntil).replace(' ', 'T'));
  if (isNaN(until.getTime())) return null;
  const remaining = until.getTime() - Date.now();
  return remaining <= 0 ? 0 : Math.ceil(remaining / 86_400_000);
};



